import React, { useEffect, useState } from 'react'
import { Keyboard, Platform, ScrollView } from 'react-native'

import { zodResolver } from '@hookform/resolvers/zod'
import { publishEvent } from '@zet/analytics'
import KeyBoardWrapper from '@zet/common/components/keyboardWrapper'
import LazyHOC from '@zet/common/components/lazyHoc'
import ScreenViewEventWrapper from '@zet/common/components/screenViewEventWrapper'
import { useModal } from '@zet/common/hooks/useModal'
import useAddCustomerStore from '@zet/customer/src/store/addCustomer'
import {
  B1,
  B2,
  B5,
  BottomSheet,
  Button,
  H5,
  Icon,
  Input,
  SH1,
  Stack,
  XStack,
  colors as zetColors,
} from '@zet/design-system'
import Pressable from '@zet/design-system/pressable'
import { Select } from '@zet/design-system/select'
import { i18n } from '@zet/localization'
import { DEVICE_HEIGHT } from '@zet/utils/Constants'
import { PINCODE_REGEX } from '@zet/utils/regularExpression'
import { useUnsafeVariable } from 'nativewind'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'

import { IRequestData } from '../../api/functions/customerAdditionStateMachine'
import {
  useAddCustomerNextState,
  useAddCustomerNextStateWeb,
  useFetchFieldsByCriteria,
} from '../../api/hooks'
import { useGetCriteriaIndexes } from '../../api/hooks/webFlow'
import AddCustomerBackHandlerHOC from '../../components/addCustomerBackHandlerHOC'

const validationSchema = z
  .object({
    pinCode: z
      .string()
      .min(1, { message: 'Field is required!' })
      .length(6, { message: 'Not a valid PIN code' })
      .regex(PINCODE_REGEX, 'Invalid Pin Code'),
    salaryType: z.string({ required_error: 'Please select a type' }),
    employmentType: z.string({ required_error: 'Please select a type' }),
    income: z
      .string()
      .min(1, { message: 'Income is required' })
      .max(99999999, { message: 'Maximum limit exceeded' })
      .regex(/^([1-9]\d*)(\.\d+)?$/, {
        message: 'Please enter valid income',
      }),
  })
  .required()
const CustomerBasicDetailsV2 = ({
  route,
}: {
  route: {
    params: {
      customerId: number
      isDemosale?: boolean
    }
  }
}) => {
  let hash
  let cusId
  const {
    params: { isDemoSale = false },
  } = route
  const { openModal, closeModal } = useModal()
  const [salaryType, setSalaryType] = useState<string>('')
  const [employmentType, setEmploymentType] = useState<string>('')
  const [isEmploymentSheetVisible, setIsEmploymentSheetVisible] =
    useState<boolean>(false)
  const [annualIncome, setAnnualIncome] = useState<string>('')
  function selectEmploymentType(value: string) {
    setIsEmploymentSheetVisible(false)
    setValue('employmentType', value, {
      shouldValidate: true,
      shouldTouch: true,
    })
    setEmploymentType(value)
  }

  const { data: EmploymentFields } = useFetchFieldsByCriteria('employmentType')
  const { data: criteriaData, refetch } = useGetCriteriaIndexes({
    hash: hash,
    locale: i18n.locale,
  })

  const { customerId } = useAddCustomerStore()
  const [colorPrimary100] = useUnsafeVariable('--color-primary-100')
  type ValidationSchema = z.infer<typeof validationSchema>
  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    getValues,
    setValue,
    setError,
  } = useForm<ValidationSchema>({
    mode: 'onChange',
    resolver: zodResolver(validationSchema),
  })

  const { mutate: mutateWithExperiments, isLoading: isExperimentsLoading } =
    useAddCustomerNextState()
  const { mutate: mutateWithExperimentsForWeb, isLoading: experimentLoading } =
    useAddCustomerNextStateWeb()

  useEffect(() => {
    return () => closeModal()
  }, [])

  const getSelectedInputLabel = (val: string) => {
    const employmentData = criteriaData?.find(
      (value) => value.criteriaName === 'employmentType',
    )
    const employmentD = employmentData?.fields?.find(
      (value) => value?.value === val,
    )
    return employmentD
  }

  const onSubmit = () => {
    const { pinCode, salaryType, employmentType, income } = getValues()

    let employmentObj
    let employmentD
    let requestBodyData
    if (Platform.OS === 'android') {
      employmentObj = EmploymentFields?.fields?.find(
        (item) => item?.displayValue === employmentType,
      )
      requestBodyData = {
        customerId: route?.params?.customerId,
        modeOfIncome: salaryType === 'Cash' ? 2 : 1,
        pinCode: Number(pinCode),
        employmentType: employmentObj?.key,
        annualIncome: Math.trunc(Number(income)),
        screenName: 'SHOWN_BASIC_DETAILS_SCREEN_V2',
        isDemoSale: isDemoSale,
      }
      publishEvent('click_action', {
        item_name: 'new_add_customer_basic_details',
        customer_id: route?.params?.customerId,
      })
      mutateWithExperiments(requestBodyData as IRequestData, {
        onSuccess: (data) => {
          if (data?.code === 400) {
            setError('pinCode', {
              message: data?.message,
            })
          }
        },
      })
    } else if (Platform.OS === 'web') {
      const urlParams = new URLSearchParams(window.location.search)
      hash = urlParams.get('hashNew')
      cusId = urlParams.get('customerId')
      requestBodyData = {
        hash: hash,
        customerId: customerId || cusId,
        modeOfIncome: salaryType === 'Cash' ? 2 : 1,
        pinCode: Number(pinCode),
        employmentType: Number(getSelectedInputLabel(employmentType)?.key),
        annualIncome: Math.trunc(Number(income)),
        screenName: 'SHOWN_BASIC_DETAILS_SCREEN_V2',
      }
      mutateWithExperimentsForWeb(requestBodyData as IRequestData, {
        onSuccess: (data) => {
          if (data?.code === 400) {
            setError('pinCode', {
              message: data?.message,
            })
          }
        },
      })
    }
  }

  return (
    <LazyHOC>
      <ScreenViewEventWrapper screenName={'web_new_add_customer_basic_details'}>
        <AddCustomerBackHandlerHOC isDemoSale={isDemoSale}>
          <Stack className="border-grays-30 border-b bg-white px-3  py-2">
            <H5>
              {Platform.OS === 'web'
                ? i18n.t('webFlow.basicDetails.pleaseEnter')
                : i18n.t('customerDetails.basicCustomerDetails')}
            </H5>
          </Stack>
          <KeyBoardWrapper
            className="flex-1 bg-white"
            BottomSheet={
              <Stack
                className="border-t-grays-30 border-t px-3"
                style={{ paddingVertical: 12 }}
              >
                <Button
                  size="lg"
                  className={`w-full`}
                  disabled={
                    !isValid || isExperimentsLoading || experimentLoading
                  }
                  style={{
                    backgroundColor:
                      !isValid || isExperimentsLoading || experimentLoading
                        ? zetColors.grays[50]
                        : colorPrimary100,
                  }}
                  loading={isExperimentsLoading || experimentLoading}
                  onPress={handleSubmit(onSubmit)}
                  loaderText={i18n.t('customerDetails.verifying')}
                >
                  {i18n.t('bottomView.continue')}
                </Button>
              </Stack>
            }
          >
            <Stack className="mx-3 mt-4">
              <B2 className="text-grays-80 mb-2">
                {Platform.OS === 'web'
                  ? i18n.t('webFlow.basicDetails.pinCode')
                  : i18n.t('customerDetails.pin')}
                <B1 className="text-error-100 pl-1"> *</B1>
              </B2>

              <Controller
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <Input
                    noIconPadding={false}
                    iconLeft={
                      <Icon
                        type="LocationPointerOutlined"
                        color={zetColors.grays[50]}
                      ></Icon>
                    }
                    placeholder="Eg: 560035"
                    onBlur={onBlur}
                    onChangeText={(value) => {
                      onChange(value)
                    }}
                    size="md"
                    keyboardType="numeric"
                    value={value}
                    maxLength={6}
                  />
                )}
                name="pinCode"
              />
              {errors.pinCode?.message ? (
                <Stack className="mt-1">
                  <B5 className="text-error-100">{errors.pinCode?.message}</B5>
                </Stack>
              ) : null}
            </Stack>

            {Platform.OS === 'android' ? (
              <>
                <Stack className="mx-3 mt-4">
                  <B2 className="text-grays-80 mb-2">
                    {i18n.t('employmentDetails.type')}
                    <B1 className="text-error-100 pl-1"> *</B1>
                  </B2>

                  <Controller
                    control={control}
                    render={({ field: { onBlur, value } }) => (
                      <Pressable
                        onPress={() => {
                          Keyboard.dismiss()
                          setIsEmploymentSheetVisible(true)
                        }}
                      >
                        <Input
                          isEditable={false}
                          placeholder={i18n.t('employmentDetails.egSelfEmp')}
                          onBlur={onBlur}
                          iconLeft={
                            <Stack className=" ">
                              <Icon
                                type="WorkOutlined"
                                color={zetColors.grays[80]}
                              />
                            </Stack>
                          }
                          iconRight={
                            <Icon
                              type="ArrowDown"
                              color={zetColors.grays[80]}
                              className="mr-1"
                            />
                          }
                          className="justify-center"
                          style={{
                            color: zetColors.grays[100],
                            fontFamily: 'Graphie-Book',
                          }}
                          value={value}
                        />
                      </Pressable>
                    )}
                    name="employmentType"
                  />
                  {errors.employmentType?.message ? (
                    <Stack className="-mt-3 mb-3">
                      <B5 className="text-error-100">
                        {errors.employmentType?.message}
                      </B5>
                    </Stack>
                  ) : null}
                </Stack>
                <BottomSheet
                  visible={isEmploymentSheetVisible}
                  disableBottomSheet={() => setIsEmploymentSheetVisible(false)}
                  onDismiss={() => setIsEmploymentSheetVisible(false)}
                  sheetName="add_customer_employment_details_type"
                >
                  <>
                    <XStack className="border-grays-30 justify-between border-b px-3 py-2 ">
                      <XStack className=" items-center  ">
                        <Icon type="WorkOutlined" size={24} />
                        <SH1 className=" ml-[12] ">
                          {i18n.t('employmentDetails.type')}
                        </SH1>
                      </XStack>
                      <Pressable
                        onPress={() => setIsEmploymentSheetVisible(false)}
                      >
                        <Icon size={24} type="Close" />
                      </Pressable>
                    </XStack>
                    <ScrollView
                      style={{
                        maxHeight: DEVICE_HEIGHT - 200,
                      }}
                    >
                      <Stack className="px-3 pb-4">
                        <Stack className="">
                          <Select onChange={selectEmploymentType}>
                            {EmploymentFields?.fields?.map(
                              ({ key, displayValue }) => (
                                <Select.Option
                                  key={key}
                                  name={displayValue}
                                  selectedValue={employmentType}
                                  optionContainerStyle="h-[62] mt-3 w-full justify-center rounded-lg"
                                >
                                  <SH1>{displayValue}</SH1>
                                </Select.Option>
                              ),
                            )}
                          </Select>
                        </Stack>
                      </Stack>
                    </ScrollView>
                  </>
                </BottomSheet>
              </>
            ) : (
              <Stack className="mx-3 mt-4">
                <B2 className="text-grays-80">
                  {i18n.t('webFlow.employmentDetails.type')}
                  <B1 className="text-error-100 pl-1">*</B1>
                </B2>
                <Controller
                  control={control}
                  render={({ field: { onBlur, value } }) => (
                    <Pressable
                      onPress={() =>
                        openModal('WebFlowSelectorModal', {
                          title: i18n.t('webFlow.employmentDetails.type'),
                          iconName: 'WorkOutlined',
                          criteriaName: 'employmentType',
                          options: criteriaData,
                          setValue: setValue,
                          selectName: 'value',
                        })
                      }
                    >
                      <Input
                        isEditable={false}
                        placeholder={i18n.t('employmentDetails.type')}
                        onBlur={onBlur}
                        iconLeft={
                          <Icon
                            type="WorkOutlined"
                            color={zetColors.grays[80]}
                          />
                        }
                        size="md"
                        iconRight={
                          <Icon
                            type="ArrowDown"
                            color={zetColors.grays[80]}
                            className="mr-1 mt-1"
                          />
                        }
                        className="justify-center"
                        style={{
                          color: zetColors.grays[100],
                          fontFamily: 'Graphie-Book',
                        }}
                        value={getSelectedInputLabel(value)?.displayValue}
                        // showErrorText={errors.employmentType?.message}
                      />
                    </Pressable>
                  )}
                  name="employmentType"
                />
                {errors.employmentType?.message ? (
                  <Stack className="mb-3 mt-1">
                    <B5 className="text-error-100">
                      {errors.employmentType?.message}
                    </B5>
                  </Stack>
                ) : null}
              </Stack>
            )}

            <Stack
              className={`${Platform.OS === 'web' ? 'mt-3' : 'mt-4'} mx-3`}
            >
              <B2 className="text-grays-80 mb-1">
                {Platform.OS === 'web'
                  ? i18n.t('webFlow.basicDetails.income')
                  : i18n.t('customerDetails.income')}
                <B1 className="text-error-100 ml-1"> *</B1>
              </B2>
              <XStack>
                <Controller
                  control={control}
                  render={({ field: { onChange, onBlur } }) => (
                    <XStack>
                      <Pressable
                        onBlur={onBlur}
                        onPress={() => {
                          Keyboard.dismiss()
                          onChange('Bank')
                          setSalaryType('Bank')
                        }}
                        eventName="click_action"
                        eventPayload={{
                          item_name:
                            Platform.OS === 'android'
                              ? 'new_add_customer_income_mode'
                              : 'web_new_add_customer_income_mode',
                          income_type: 'Bank',
                        }}
                      >
                        <XStack
                          className={`px-4/2 mr-3 items-center rounded-xl border py-2
             ${
               'Bank' !== salaryType
                 ? 'border-grays-40'
                 : 'border-primary-100 bg-grays-20'
             }
            `}
                        >
                          {salaryType !== 'Bank' ? (
                            <Icon type="BankAccount" size={36}></Icon>
                          ) : (
                            <Icon type="BankAccountWithColor" size={36}></Icon>
                          )}
                          <B2 className="text-grays-60 ml-2">
                            {i18n.t('customerDetails.bank')}
                          </B2>
                        </XStack>
                      </Pressable>
                      <Pressable
                        onBlur={onBlur}
                        onPress={() => {
                          Keyboard.dismiss()
                          onChange('Cash')
                          setSalaryType('Cash')
                        }}
                        eventName="click_action"
                        eventPayload={{
                          item_name:
                            Platform.OS === 'android'
                              ? 'new_add_customer_income_mode'
                              : 'web_new_add_customer_income_mode',
                          income_type: 'Cash',
                        }}
                      >
                        <XStack
                          className={`px-4/2 mr-3 items-center rounded-xl border py-2
             ${
               'Cash' !== salaryType
                 ? 'border-grays-40'
                 : 'border-primary-100 bg-grays-20'
             }
            `}
                        >
                          {salaryType !== 'Cash' ? (
                            <Icon type="Cash" size={36}></Icon>
                          ) : (
                            <Icon type="CashWithColor" size={36}></Icon>
                          )}
                          <B2 className="text-grays-60 ml-2">
                            {i18n.t('customerDetails.cash')}
                          </B2>
                        </XStack>
                      </Pressable>
                    </XStack>
                  )}
                  name="salaryType"
                />
              </XStack>
              {errors.salaryType?.message ? (
                <Stack className="mt-1">
                  <B5 className="text-error-100">
                    {errors.salaryType?.message}
                  </B5>
                </Stack>
              ) : null}
            </Stack>

            <Stack className="mx-3 my-4">
              <B2 className="text-grays-80 mb-1">
                {Platform.OS === 'web'
                  ? i18n.t('webFlow.employmentDetails.annual')
                  : i18n.t('employmentDetails.annual')}
                <B1 className="text-error-100 pl-1">*</B1>
              </B2>

              <Controller
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <Input
                    maxLength={8}
                    placeholder="Eg: ₹14,475"
                    onBlur={onBlur}
                    size="md"
                    onChangeText={(value) => {
                      onChange(value)
                      setAnnualIncome(value)
                    }}
                    keyboardType="number-pad"
                    value={annualIncome}
                    type={`${errors.income?.message ? 'error' : 'primary'}`}
                  />
                )}
                name="income"
              />
              {errors.income?.message ? (
                <Stack className="mt-1">
                  <B5 className="text-error-100">{errors.income?.message}</B5>
                </Stack>
              ) : null}
            </Stack>
          </KeyBoardWrapper>
        </AddCustomerBackHandlerHOC>
      </ScreenViewEventWrapper>
    </LazyHOC>
  )
}

export default CustomerBasicDetailsV2
